import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import {
  EXISTING_LIEFERSCHEIN_WARNING,
  type ExistingLieferscheinHint,
} from "@/pages/dokumente/existingLieferscheinHint";

export function ExistingLieferscheinAlert({
  items,
  className,
  onOpenNewest,
}: {
  items: ExistingLieferscheinHint[];
  className?: string;
  onOpenNewest?: () => void;
}) {
  if (!items.length) return null;

  return (
    <Alert variant="warning" title="Lieferschein bereits vorhanden" className={className}>
      <p>{EXISTING_LIEFERSCHEIN_WARNING}</p>
      <ul className="mt-2 list-inside list-disc text-xs text-label-secondary">
        {items.slice(0, 6).map((item) => (
          <li key={item.id}>
            {item.label}
            {item.statusLabel ? ` · ${item.statusLabel}` : ""}
          </li>
        ))}
        {items.length > 6 ? <li>… und {items.length - 6} weitere</li> : null}
      </ul>
      {onOpenNewest ? (
        <div className="mt-3">
          <Button type="button" size="sm" variant="secondary" onClick={onOpenNewest}>
            Vorhandenen Lieferschein öffnen
          </Button>
        </div>
      ) : null}
    </Alert>
  );
}

/** Bestätigung, bevor ein weiterer Lieferschein zum selben Projekt entsteht. */
export function ExistingLieferscheinDialog({
  open,
  busy = false,
  items,
  onClose,
  onContinue,
  onOpenExisting,
  zIndexClass,
}: {
  open: boolean;
  busy?: boolean;
  items: ExistingLieferscheinHint[];
  onClose: () => void;
  onContinue: () => void;
  onOpenExisting?: (id: number) => void;
  zIndexClass?: string;
}) {
  const newest = items[0] ?? null;
  const canOpenExisting = Boolean(onOpenExisting && newest);

  return (
    <Dialog
      open={open}
      onClose={busy ? () => undefined : onClose}
      title="Lieferschein bereits vorhanden"
      size="md"
      zIndexClass={zIndexClass}
      plainBackdrop
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button
            type="button"
            variant={canOpenExisting ? "secondary" : "primary"}
            onClick={onContinue}
            disabled={busy}
            loading={busy}
          >
            Trotzdem erstellen
          </Button>
          {canOpenExisting && newest && onOpenExisting ? (
            <Button type="button" onClick={() => onOpenExisting(newest.id)} disabled={busy}>
              Vorhandenen Lieferschein öffnen
            </Button>
          ) : null}
        </div>
      }
    >
      <ExistingLieferscheinAlert items={items} />
      {items.length > 1 && canOpenExisting ? (
        <p className="mt-3 text-xs text-label-secondary">
          Geöffnet wird der zuletzt geänderte Lieferschein. Die übrigen bleiben im Projekt erhalten.
        </p>
      ) : null}
    </Dialog>
  );
}
